<template>
  <div>
    <h1 class="flex mb-8 font-bold text-3xl group">
      <inertia-link
        class="flex text-indigo-400 group-hover:text-indigo-600"
        :href="route('questionnaires')"
      >
        <icon
          name="questionnaires"
          class="w-6 h-9 mr-3 fill-indigo-400 group-hover:fill-indigo-600"
        />
        Questionários /
      </inertia-link>
      <span class="ml-1 font-medium"> {{ title }} </span>
    </h1>

    <div class="bg-white rounded-md shadow overflow-hidden w-full">
      <form @submit.prevent="update">
        <h1 class="p-8 my-4 text-xl">Configuração de relatório completo</h1>
        <div class="p-8">
          <textarea-input label="Introdução" v-model="intro_report" />
        </div>
        <div
          v-for="topic in questionnaire.topics"
          :key="`topic-${topic.id}`"
          class="p-8 -mb-8 text-center"
        >
          <h2 class="text-2xl font-bold mt-2 mb-6">
            <font-awesome-icon :icon="topic.icon" size="lg" /> {{ topic.name }}
          </h2>

          <div
            v-for="question in topic.questions"
            :key="`question-${question.id}`"
            class="placeholder-indigo-500"
          >
            <div class="flex flex-wrap">
              <v-md-preview :text="question.statement" class="text-xl font-sans mx-auto -mb-8" />
            </div>

            <small>Questão do tipo "{{ handleQuestionType(question.type) }}"</small>

            <div class="flex flex-wrap my-6 relative">
              <div
                class="text-left"
                :class="{
                  'w-full': !form[question.id].chart,
                  'w-1/4': form[question.id].chart,
                }"
                style="max-height: 400px"
              >
                <select-input
                  v-model="form[question.id].chart"
                  label="Gráfico"
                  class="mb-4"
                  @change="removeChartIfPresent(question)"
                >
                  <option :value="null">Não</option>

                  <option v-if="question.type !== 'LINE' && question.type !== 'MULTI'" value="BAR">
                    Barras
                  </option>

                  <option v-if="question.type !== 'LINE' && question.type !== 'MULTI'" value="COL">
                    Colunas
                  </option>

                  <option v-if="question.type !== 'LINE' && question.type !== 'MULTI'" value="PIE">
                    Pizza
                  </option>

                  <option
                    v-if="question.type !== 'LINE' && question.type !== 'MULTI'"
                    value="RADAR"
                  >
                    Área
                  </option>

                  <option v-if="question.type === 'LINE' || question.type === 'MULTI'" value="LIST">
                    Lista
                  </option>
                </select-input>

                <select-input
                  v-if="
                    form[question.id].chart && question.type !== 'LINE' && question.type !== 'MULTI'
                  "
                  id="chart-value"
                  v-model="form[question.id].value"
                  label="Tipo de Valor"
                  :class="{
                    'mb-4': form[question.id].chart === 'RADAR',
                  }"
                >
                  <option :value="null" />

                  <option value="PERCENT">Porcentagem</option>

                  <option value="QUANTITY">Quantidade</option>
                </select-input>

                <text-input
                  v-if="
                    form[question.id].chart && question.type !== 'LINE' && question.type !== 'MULTI'
                  "
                  v-model="form[question.id].color"
                  label="Cor"
                  type="color"
                  :class="{
                    hidden: form[question.id].chart !== 'RADAR',
                  }"
                />

                <ul
                  v-if="
                    form[question.id].chart &&
                    form[question.id].value &&
                    question.type !== 'LINE' &&
                    question.type !== 'MULTI'
                  "
                  class="mt-4 overflow-y-auto"
                  :style="{
                    'max-height': `${form[question.id].chart !== 'RADAR' ? 250 : 160}px`,
                  }"
                >
                  <li
                    v-for="option in question.options"
                    :key="`option-${question.id}-${
                      question.type === 'NUMBER' ? option : option.id
                    }`"
                    class="flex flex-wrap mb-6"
                  >
                    <text-input
                      v-model="
                        form[question.id].labels[question.type === 'NUMBER' ? option : option.id]
                      "
                      :label="question.type === 'NUMBER' ? `Nota ${option}` : option.alternative"
                      type="text"
                      class="mr-auto"
                      :class="{
                        'w-1/2': form[question.id].chart !== 'RADAR',
                        'w-full': form[question.id].chart === 'RADAR',
                      }"
                    />

                    <text-input
                      v-model="
                        form[question.id].colors[question.type === 'NUMBER' ? option : option.id]
                      "
                      label="Cor"
                      type="color"
                      :class="{
                        'w-1/3': form[question.id].chart !== 'RADAR',
                        hidden: form[question.id].chart === 'RADAR',
                      }"
                    />
                  </li>
                </ul>
              </div>

              <!-- RENDERIZANDO O GRÁFICO -->
              <div
                v-if="
                  form[question.id].chart &&
                  form[question.id].chart !== 'LIST' &&
                  form[question.id].value &&
                  question.type !== 'LINE' &&
                  question.type !== 'MULTI'
                "
                class="w-3/4 bg-red-500"
              >
                <highcharts :options="charts.get(question)" />
              </div>

              <button
                v-if="
                  form[question.id].chart &&
                  form[question.id].chart !== 'LIST' &&
                  form[question.id].value &&
                  question.type !== 'LINE' &&
                  question.type !== 'MULTI'
                "
                class="btn-indigo absolute bottom-0 right-0 mr-2"
                type="button"
                @click="charts.set(question)"
              >
                <icon class="w-5 h-5 fill-white" name="return" />
              </button>

              <div
                v-if="
                  form[question.id].chart && (question.type === 'LINE' || question.type === 'MULTI')
                "
                class="w-3/4 overflow-y-auto overflow-x-hidden"
                style="max-height: 400px"
              >
                <table class="w-full mx-3 whitespace-nowrap">
                  <tr class="text-left font-bold">
                    <th class="px-6 pt-6 pb-4">Resposta</th>
                    <th class="px-6 pt-6 pb-4">Data</th>
                  </tr>

                  <tr
                    v-for="item in list"
                    :key="item.text"
                    class="text-left hover:bg-gray-100 focus-within:bg-gray-100"
                  >
                    <td class="border-t px-2 pt-2 pb-1">
                      {{ item.text }}
                    </td>
                    <td class="border-t px-2 pt-2 pb-1">
                      {{ item.date }}
                    </td>
                  </tr>
                </table>
              </div>
            </div>
            <div class="text-justify">
              <div v-if="rules[question.id]">
                <!-- Lista de insights -->
                <h3 class="my-6 text-2xl">Lista de Insights</h3>
                <Accordion>
                  <AccordionTab v-for="rule in rules[question.id]" :key="rule.rule_id">
                    <template #header>
                      <p>{{ rule.rule_title }}</p>
                    </template>

                    <div v-if="isEditingInsight && id_of_insight_beeing_edited == rule.rule_id">
                      <text-input
                        label="Título do insight"
                        type="text"
                        class="my-3"
                        v-model="currentRuleEDITED.title"
                      />
                      <div class="my-4">
                        <textarea-input
                          label="Fale com o GPT"
                          v-model="currentRuleEDITED.question"
                        />
                      </div>
                      <div class="my-5">
                        <p>Variáveis</p>
                        <li v-for="field in fields.vars" :key="field.value" class="my-2">
                          <strong>{{ field.value }}</strong
                          >: {{ field.label }}
                        </li>
                        <template v-for="(field, index) in getVars[question.id]">
                          <li :key="`L-${field[0]}`" class="my-2">
                            <strong>[OPÇÃO{{ field[0] }}]</strong>: Essa chave é substituída pelo
                            nome usado no gráfico da opção "{{ field[1] }}"
                          </li>
                          <li :key="`V-${field[0]}`" class="my-2">
                            <strong>[VALOR{{ field[0] }}]</strong>: Essa chave é substituída pelo
                            valor apresentado no gráfico da opção "{{ field[1] }}"
                          </li>
                        </template>
                      </div>
                      <div class="my-5">
                        <button type="button" class="btn-indigo" @click="confirmInsightEdit()">
                          Salvar
                        </button>
                        <button type="button" class="btn-gray" @click="cancelInsightEdit">
                          Cancelar
                        </button>
                      </div>
                    </div>

                    <div v-else>
                      <div class="my-6">
                        <p class="font-semibold my-3">Pergunta ao chat GPT:</p>
                        <p>{{ rule.gpt_question }}</p>
                      </div>
                      <div
                        class="my-4 border-t border-gray-50"
                        v-if="!(rule.gpt_answer == 'teste')"
                      >
                        <p class="font-semibold my-3">Insight gerado:</p>
                        <p>{{ rule.gpt_answer }}</p>
                      </div>
                      <div>
                        <button
                          type="button"
                          class="btn-indigo"
                          @click="startEditInsight(rule, question)"
                        >
                          Editar
                        </button>
                        <button type="button" class="btn-gray" @click="deleteInsight(rule.rule_id)">
                          Deletar
                        </button>
                      </div>
                    </div>
                  </AccordionTab>
                </Accordion>
              </div>
              <br /><br />
              <button
                class="btn-indigo"
                type="button"
                @click="createRule(question)"
                :disabled="isCreatingRule"
                v-if="form[question.id].chart"
              >
                Gerar prompt
              </button>
              <button
                class="btn-gray"
                type="button"
                @click="cancelCreation"
                v-if="question.id == creatingRule_forQuestion && isCreatingRule"
              >
                Cancelar
              </button>
              <!-- Criar insight -->
              <div class="my-4" v-if="question.id == creatingRule_forQuestion && isCreatingRule">
                <div>
                  <text-input
                    label="Título do insight"
                    type="text"
                    class="my-3"
                    v-model="currentRule.title"
                  />
                </div>
                <div class="my-4">
                  <textarea-input label="Fale com o GPT" v-model="currentRule.question" />
                </div>
                <div class="my-5">
                  <p>Variáveis</p>
                  <li v-for="field in fields.vars" :key="field.value" class="my-2">
                    <strong>{{ field.value }}</strong
                    >: {{ field.label }}
                  </li>
                  <template v-for="(field, index) in getVars[question.id]">
                    <li :key="`L-${field[0]}`" class="my-2">
                      <strong>[OPÇÃO{{ field[0] }}]</strong>: Essa chave é substituída pelo nome
                      usado no gráfico da opção "{{ field[1] }}"
                    </li>
                    <li :key="`V-${field[0]}`" class="my-2">
                      <strong>[VALOR{{ field[0] }}]</strong>: Essa chave é substituída pelo valor
                      apresentado no gráfico da opção "{{ field[1] }}"
                    </li>
                  </template>
                </div>
                <div class="my-4">
                  <button class="btn-indigo mr-4" type="button" @click="saveInsight(question)">
                    Salvar
                  </button>
                  <!-- <button class="btn-gray" type="button" @click="generateInsightPreview(question)">
                    Gerar preview
                  </button> -->
                </div>
              </div>
            </div>
          </div>

          <hr class="mt-4" />
        </div>
        <div class="p-8">
          <textarea-input label="Conclusão" v-model="footer_report" />
        </div>
        <div
          class="px-8 py-4 mt-5 bg-gray-50 border-t border-gray-100 flex justify-end items-center"
        >
          <loading-button :loading="form.processing" class="btn-indigo" type="submit">
            Salvar
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Layout from '@/Shared/Layout'
import Icon from '@/Shared/Icon'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'
import { Chart as highcharts } from 'highcharts-vue'
import HighCharts from 'highcharts'
import HighchartsMore from 'highcharts/highcharts-more'
import moment from 'moment'
import LoadingButton from '@/Shared/LoadingButton'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'

export default {
  metaInfo() {
    return {
      title: this.title,
    }
  },
  components: {
    Icon,
    SelectInput,
    TextInput,
    TextareaInput,
    highcharts,
    LoadingButton,
    Accordion,
    AccordionTab,
  },
  layout: Layout,
  remember: 'form',
  props: {
    questionnaire: Object,
    data: Object,
    rules: Object,
    fields: Object,
    vars: Object,
  },
  data() {
    return {
      title: this.questionnaire.title,
      form: (() => {
        const form = {
          _method: 'put',
          intro_report: '',
          footer_report: '',
        }

        const color = () => '#000000'.replace(/0/g, () => (~~(Math.random() * 16)).toString(16))

        for (const topic of this.questionnaire.topics) {
          for (const question of topic.questions) {
            form[question.id] = {
              chart: this.data?.[question.id]?.chart ?? null,
              value: this.data?.[question.id]?.value ?? null,
              color: this.data?.[question.id]?.color ?? color(),
            }

            if (question.type !== 'LINE' && question.type !== 'MULTI') {
              form[question.id].labels = {}
              form[question.id].colors = {}

              for (const option of question.options) {
                const id = question.type === 'NUMBER' ? option : option.id

                form[question.id].labels[id] =
                  this.data?.[question.id]?.labels?.[id] ??
                  (question.type === 'NUMBER' ? `Nota ${option}` : option.alternative)

                form[question.id].colors[id] = this.data?.[question.id]?.colors?.[id] ?? color()
              }
            }
          }
        }
        return this.$inertia.form(form)
      })(),
      charts: (() => {
        const chart = this.chart

        const charts = {
          _: {},
          get: function (question) {
            if (this._[question.id]) {
              return this._[question.id]
            }

            this.set(question)

            return this.get(question)
          },
          set: function (question) {
            this._[question.id] = chart(question)
          },
          del: function (question) {
            this._[question.id] = null
          },
        }

        for (const topic of this.questionnaire.topics) {
          for (const question of topic.questions) {
            charts._[question.id] = null
          }
        }

        return charts
      })(),
      get list() {
        const list = []
        const range = Math.floor(Math.random() * (50 - 15 + 1) + 15)

        moment.locale('pt-br')

        for (let i = 0; i < range; i++) {
          list.push({
            text: `Resposta ${'0000000000000000'.replace(/0/g, () =>
              (~~(Math.random() * 16)).toString(16),
            )}`,
            date: moment(
              (() => {
                const end = new Date()
                const start = new Date()

                start.setDate(end.getDate() - 7)

                return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()))
              })(),
            ).format('DD/MM/YYYY HH:mm'),
          })
        }

        return list
      },
      isCreatingRule: false,
      creatingRule_forQuestion: null,
      procedures: [
        {
          value: 'Sugestão',
          key: 'SUGGESTION',
        },
        {
          value: 'Ação',
          key: 'ACTION',
        },
        {
          value: 'Política Pública',
          key: 'POLICY',
        },
        {
          value: 'Predição',
          key: 'PREDICTION',
        },
        {
          value: 'Inteligência de Mercado',
          key: 'INTELLIGENCE',
        },
        {
          value: 'Análise',
          key: 'ANALYSIS',
        },
        {
          value: 'Outro Procedimento',
          key: '',
        },
      ],
      currentRule: {
        title: null,
        question: null,
        answer: null,
        procedure: null,
      },
      isEditingInsight: false,
      id_of_insight_beeing_edited: null,
      currentRuleEDITED: {
        title: null,
        question: null,
        answer: null,
        procedure: null,
        id: null,
        vars: [],
      },
      intro_report: '',
      footer_report: '',
      loading_creation_insight: false,

      variables: [],
    }
  },
  mounted() {
    setTimeout(() => {
      for (const preview of document.getElementsByClassName('v-md-editor-preview')) {
        for (const child of preview.getElementsByTagName('*')) {
          child.classList.add('font-sans', 'text-xl')
        }
      }
    }, 2.5e2)
    this.intro_report = this.questionnaire.intro_report
    this.footer_report = this.questionnaire.footer_report
  },
  computed: {
    getVars() {
      let vec = this.variables
      return vec
    },
  },
  methods: {
    update() {
      this.form.footer_report = this.footer_report
      this.form.intro_report = this.intro_report
      this.form.post(this.route('questionnaires.configure', this.questionnaire.id).url())
    },
    chart(question) {
      if (question.type === 'LINE' || question.type === 'MULTI') {
        return {}
      }

      const form = this.form[question.id]
      const chart = form.chart
      const value = form.value
      const color = form.color

      const isPieChart = chart === 'PIE'
      const isRadarChart = chart === 'RADAR'
      const isPercentage = value === 'PERCENT'

      const labels = form.labels
      const colors = form.colors

      const isNumberQuestion = question.type === 'NUMBER'

      const n = isPercentage ? 100 : Math.floor(Math.random() * (5000 - 100 + 1) + 100)

      const options = {
        chart: {
          type: (() => {
            switch (chart) {
              case 'BAR':
                return 'bar'
              case 'COL':
                return 'column'
              case 'PIE':
                return 'pie'
              case 'RADAR':
                return 'area'
            }
          })(),
          polar: isRadarChart,
        },
        title: {
          text: '',
        },
        ...(() => {
          const pane = {}

          if (isRadarChart) {
            pane.pane = {
              startAngle: 0,
              endAngle: 360,
            }
          }

          return pane
        })(),
        legend: {
          enabled: !isRadarChart,
        },
        xAxis: (() => {
          const axis = {}

          if (isRadarChart) {
            axis.categories = []

            for (const option of question.options) {
              axis.categories.push(labels[isNumberQuestion ? option : option.id])
            }

            return axis
          }

          axis.labels = {
            enabled: false,
          }

          return axis
        })(),
        yAxis: (() => {
          if (isRadarChart) {
            return {
              type: 'logarithmic',
              max: n,
              labels: {
                formatter: () => '',
              },
            }
          }

          return {
            min: 0,
            labels: {
              format: isPercentage ? '{value:.1f}%' : '{value}',
            },
            title: {
              enabled: false,
            },
          }
        })(),
        tooltip: (() => {
          const pointY = isPercentage ? '{point.y:.1f}%' : '{point.y}'

          const pointFormat =
            isPieChart || isRadarChart
              ? `<b>${pointY}</b>`
              : `<tr><td style="padding:0">{series.name}:</td></tr><tr><td style="padding:0"><b>${pointY}</b></td></tr>`

          if (isPieChart) {
            return {
              pointFormat,
            }
          }

          return {
            headerFormat: '<table>',
            pointFormat,
            footerFormat: '</table>',
            useHTML: true,
          }
        })(),
        series: (() => {
          const series = []
          const parts = (() => {
            const iterations = question.options.length
            const parts = []
            let remainder = n

            for (let i = 1; i <= iterations; i += 1) {
              if (i === iterations) {
                parts.push(remainder)

                break
              }

              const part = Math.round(Math.random() * remainder)

              parts.push(part)

              remainder -= part
            }

            return parts
          })()

          if (isRadarChart) {
            const serie = {
              name: '',
              type: 'area',
              color: color,
              data: [],
            }

            for (const i in question.options) {
              const option = question.options[i]

              serie.data.push(
                isNumberQuestion
                  ? Number(i) === option - 1
                    ? parts[option - 1]
                    : 0
                  : question.options[i].id === option.id
                  ? parts[i]
                  : 0,
              )
            }

            series.push(serie)

            return series
          }

          if (isPieChart) {
            const serie = {
              name: '',
              colorByPoint: true,
              data: [],
            }

            for (const i in question.options) {
              const option = question.options[i]
              const id = isNumberQuestion ? option : option.id

              const data = {
                name: labels[id],
                color: colors[id],
              }

              if (isNumberQuestion) {
                data.y = Number(i) === option - 1 ? parts[option - 1] : 0

                serie.data.push(data)

                continue
              }

              data.y = question.options[i].id === option.id ? parts[i] : 0

              serie.data.push(data)
            }

            series.push(serie)

            return series
          }

          for (const option of question.options) {
            const id = isNumberQuestion ? option : option.id

            const serie = {
              name: labels[id],
              color: colors[id],
              data: [],
            }

            for (const i in question.options) {
              serie.data[i] = isNumberQuestion
                ? Number(i) === option - 1
                  ? parts[option - 1]
                  : 0
                : question.options[i].id === option.id
                ? parts[i]
                : 0
            }

            series.push(serie)
          }

          return series
        })(),
      }

      return options
    },
    handleQuestionType(type) {
      switch (type) {
        case 'NUMBER':
          return 'Nota (1 a 5)'
        case 'RADIO':
          return 'Escolha Única'
        case 'CHECK':
          return 'Seleção Múltipla'
        case 'LINE':
          return 'Texto de Linha Única'
        case 'MULTI':
          return 'Texto Multilinha'
        case 'SLIDER':
          return 'Controle Deslizante'
        default:
          return type
      }
    },
    removeChartIfPresent(question) {
      if (question.type === 'LINE' || question.type === 'MULTI') {
        return
      }

      if (this.charts.get(question)) {
        this.charts.del(question)

        this.form[question.id].value = null

        setTimeout(() => {
          const select = document.getElementById('chart-value')

          if (select) {
            select.value = ''
          }
        }, 2.5e2)

        for (const option of question.options) {
          this.form[question.id].labels[question.type === 'NUMBER' ? option : option.id] =
            question.type === 'NUMBER' ? `Nota ${option}` : option.alternative

          this.form[question.id].colors[question.type === 'NUMBER' ? option : option.id] =
            '#000000'.replace(/0/g, () => (~~(Math.random() * 16)).toString(16))
        }
      }
    },
    handleProcedureType(value) {
      switch (value) {
        case 'SUGGESTION':
          return 'Sugestão'
        case 'ACTION':
          return 'Ação'
        case 'ANALYSIS':
          return 'Análise'
        case 'POLICY':
          return 'Política Pública'
        case 'PREDICTION':
          return 'Predição'
        case 'INTELLIGENCE':
          return 'Inteligência de Mercado'
        case '':
          return 'Outro Procedimento'
      }
    },
    createRule(question) {
      // console.log(question)
      if (!this.variables[question.id]) {
        this.variables[question.id] = []
        if (question.type != 'MULTI' || question.type != 'LINE' || question.type != 'SLIDER') {
          if (question.type == 'NUMBER') {
            for (let i = 1; i <= 5; i++) {
              let string_id = question.id + `N${i}`
              let op = `Ǹota ${i}`
              let aux = []

              aux.push(string_id)
              aux.push(op)
              this.variables[question.id].push(aux)
            }
          } else {
            question.options.forEach((opt) => {
              let string_id = opt.question_id + '' + opt.id
              let op = opt.alternative
              let aux = []
              aux.push(string_id)
              aux.push(op)
              this.variables[question.id].push(aux)
            })
          }
        }
      }

      // console.log(this.variables)
      let questionID = question.id

      this.isCreatingRule = true
      this.creatingRule_forQuestion = questionID
    },
    cancelCreation() {
      this.isCreatingRule = false
      this.creatingRule_forQuestion = null

      this.currentRule.title = null
      this.currentRule.question = null
      this.currentRule.answer = null
      this.currentRule.procedure = null
    },
    generateInsightPreview(question_full) {
      if (this.currentRule.question) {
        if (this.currentRule.question.length > 0) {
          let question = this.currentRule.question
          let question_from_questionnare = {
            [this.questionnaire.id]: [question_full.id],
          }

          axios
            .post(this.route('answer.preview'), {
              question,
              _token: this.$page.props.auth.user.csrf_token,
              questionnaireQuestions: question_from_questionnare,
            })
            .then((response) => {
              console.log(response.data)
              this.currentRule.answer = response.data.answer
            })
        }
      } else {
        console.log('Por favor preencha os campos')
      }
    },
    saveInsight(question) {
      this.loading_creation_insight = true
      let rule = {
        title: {
          _type: 'title',
          text: this.currentRule.title,
        },
        icon: {
          _type: 'icon',
          text: null,
        },
        blocks: [
          [
            {
              _type: 'condition',
              if: 'INTELLIGENT',
              condition: {
                indicator: null,
                questionnaire: this.questionnaire.id,
                topic: null,
                question: question.id,
                user: null,
                organization: null,
                role: null,
                state: null,
                cities: [],
              },
              has: {
                on_value: null,
                on_topic: null,
              },
              value: {
                number: null,
                text: null,
                date: null,
                interval: {
                  first: null,
                  last: null,
                },
              },
            },
          ],
        ],
        _actions: [],
        actions: {
          _type: 'actions',
          _active: false,
          values: [],
        },
        GPT: {
          _type: 'gpt',
          _active: true,
          procedure: 'SUGGESTION',
          question: this.currentRule.question,
          answer: 'teste',
        },
      }

      axios
        .post(this.route('rules.store'), {
          ...rule,
          _token: this.$page.props.auth.user.csrf_token,
        })
        .then(async () => {
          this.loading_creation_insight = false
          this.form.footer_report = this.footer_report
          this.form.intro_report = this.intro_report
          await this.form.post(this.route('questionnaires.configure', this.questionnaire.id).url())
          this.$inertia.get(this.route('questionnaires.config', this.questionnaire.id))
        })
    },
    startEditInsight(rule, question) {
      this.isEditingInsight = true
      this.id_of_insight_beeing_edited = rule.rule_id

      if (!this.variables[question.id]) {
        this.variables[question.id] = []
        if (question.type != 'MULTI' || question.type != 'LINE' || question.type != 'SLIDER') {
          if (question.type == 'NUMBER') {
            for (let i = 1; i <= 5; i++) {
              let string_id = question.id + `N${i}`
              let op = `Ǹota ${i}`
              let aux = []

              aux.push(string_id)
              aux.push(op)
              this.variables[question.id].push(aux)
            }
          } else {
            question.options.forEach((opt) => {
              let string_id = opt.question_id + '' + opt.id
              let op = opt.alternative
              let aux = []
              aux.push(string_id)
              aux.push(op)
              this.variables[question.id].push(aux)
            })
          }
        }
      }

      this.currentRuleEDITED.title = rule.rule_title
      this.currentRuleEDITED.question = rule.gpt_question
      this.currentRuleEDITED.answer = rule.gpt_answer
      this.currentRuleEDITED.procedure = rule.gpt_questionType
      this.currentRuleEDITED.id = rule.rule_id
    },
    cancelInsightEdit() {
      this.isEditingInsight = false
      this.id_of_insight_beeing_edited = null

      this.currentRuleEDITED.title = null
      this.currentRuleEDITED.question = null
      this.currentRuleEDITED.answer = null
      this.currentRuleEDITED.procedure = null
      this.currentRuleEDITED.id = null
    },
    confirmInsightEdit() {
      const rule = {
        title: this.currentRuleEDITED.title,
        gpt_question: this.currentRuleEDITED.question,
      }

      const rule_id = this.currentRuleEDITED.id

      this.$inertia.put(this.route('questions.rules.update', [this.questionnaire.id, rule_id]), {
        ...rule,
      })
    },
    deleteInsight(rule_id) {
      this.$inertia.delete(
        this.route('questionnaires.configure.destroyrule', [this.questionnaire.id, rule_id]),
      )
    },
  },
}
</script>
